import { Result, ResultProps, Typography } from 'antd';
import { ResultStatusType } from 'antd/lib/result';
import { AxiosError } from 'axios';
import { Helmet } from 'react-helmet-async';

import { t } from '../i18n';
interface ErrorResultProps<T = AxiosError> extends Partial<ResultProps> {
  error: T | null;
  children: React.ReactNode;
}

export type ErrorResultStatusType =
  | Extract<ResultStatusType, 'error' | '404' | '403' | '500'>
  | '400'
  | '409'
  | '410'
  | '422'
  | '501'
  | '503'
  | '507';

type ErrorResultDefaults = Record<
  ErrorResultStatusType,
  Pick<ResultProps, 'status' | 'title' | 'subTitle'>
>;

export const errorResultDefaults: ErrorResultDefaults = {
  error: {
    status: 'error',
    title: t('Erro'),
    subTitle: t('Desculpe, algo deu errado.'),
  },
  '400': {
    status: 'error',
    title: t('Erro'),
    subTitle: t('Desculpe, o serviço recebeu uma solicitação inválida.'),
  },
  '404': {
    status: '404',
    title: t('Não encontrado'),
    subTitle: t('Desculpe, a página que você visitou não existe.'),
  },
  '403': {
    status: '403',
    title: t('Não autorizado'),
    subTitle: t('Desculpe, você não está autorizado a acessar esta página.'),
  },
  '409': {
    status: 'error',
    title: t('Erro'),
    subTitle: t('Desculpe, o serviço não conseguiu atender sua solicitação em tempo hábil.'),
  },
  '410': {
    status: 'error',
    title: t('Erro'),
    subTitle: t('Desculpe, a informação que você solicitou não está mais disponível.'),
  },
  '422': {
    status: 'error',
    title: t('Erro'),
    subTitle: t('Desculpe, o serviço não conseguiu processar sua solicitação.'),
  },
  '500': {
    status: '500',
    title: t('Erro do servidor'),
    subTitle: t('Desculpe, algo deu errado.'),
  },
  '501': {
    status: 'error',
    title: t('Erro'),
    subTitle: t('Desculpe, o serviço não possui essa funcionalidade implementada.'),
  },
  '503': {
    status: 'error',
    title: t('Erro'),
    subTitle: t('Desculpe, o serviço está indisponível no momento.'),
  },
  '507': {
    status: 'error',
    title: t('Erro'),
    subTitle: t(
      'Desculpe, o serviço não conseguiu atender sua solicitação por falta de armazenamento.'
    ),
  },
};

const ErrorResult = ({ error, children, ...props }: ErrorResultProps) => {
  if (error) {
    const statusCode = (error.response?.status.toString() || 'error') as ErrorResultStatusType;
    const { status, title, subTitle } =
      errorResultDefaults[statusCode] || errorResultDefaults.error;

    return (
      <>
        <Helmet>
          <title>
            {title} | {t('Recrutamento e Seleção')}
          </title>
        </Helmet>
        <Result
          status={status}
          title={title}
          subTitle={subTitle}
          extra={
            <>
              {error.response?.headers['x-elasticapm-traceid'] && (
                <>
                  <br />
                  <br />
                  <Typography.Text code>
                    Trace ID: {error.response?.headers['x-elasticapm-traceid']}
                  </Typography.Text>
                </>
              )}
            </>
          }
          {...props}
        />
      </>
    );
  }

  return <>{children}</>;
};

export default ErrorResult;
